import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, FileText, Scale, ArrowLeft } from "lucide-react";
import { useDocumentMeta } from "../hooks/useDocumentMeta";

export default function Disclaimer() {
  useDocumentMeta({
    title: "Disclaimer | Smart EMI Planner",
    description:
      "Read our disclaimer. Smart EMI Planner provides calculations for informational purposes only. Please verify calculations before making financial decisions.",
  });

  return (
    <div className="max-w-4xl mx-auto px-4 py-12 sm:px-6">
      <Link
        to="/home-loan"
        className="inline-flex items-center gap-2 text-emerald-600 font-semibold hover:text-emerald-700 mb-8 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Back to Calculator
      </Link>

      <div className="bg-white p-8 sm:p-12 rounded-3xl shadow-sm border border-slate-200/80">
        <header className="mb-10 pb-8 border-b border-slate-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-3 bg-amber-50 text-amber-600 rounded-2xl">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900">
              Disclaimer
            </h1>
          </div>
          <p className="text-slate-500 text-sm font-medium">
            Last Updated: June 7, 2026
          </p>
        </header>

        <div className="prose prose-slate prose-emerald max-w-none text-slate-600 space-y-8 font-medium">
          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <FileText className="w-5 h-5 text-amber-500" />
              1. Informational & Educational Purposes Only
            </h2>
            <p className="leading-relaxed">
              All tools, calculators, graphs, summaries, and information
              provided on <strong>Smart EMI Planner</strong>
              are for informational and educational purposes only. They are
              designed to help you simulate and visualize various loan scenarios
              (such as interest rate changes, prepayments, and tenure
              reductions).
            </p>
            <p className="leading-relaxed font-semibold text-slate-800 bg-slate-50 p-4 rounded-xl border border-slate-100">
              The results generated by these calculators are estimates and
              approximations, not exact mathematical guarantees or legally
              binding quotes.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              <Scale className="w-5 h-5 text-amber-500" />
              2. Not Professional Financial Advice
            </h2>
            <p className="leading-relaxed">
              We are not financial advisors, lenders, bankers, or certified
              legal/tax experts. The content on this website and the generated
              reports should not be construed as investment, legal, tax, or
              financial advice.
            </p>
            <p className="leading-relaxed">
              Before making any critical financial decisions (such as applying
              for a loan, finalizing prepayments, refraining from refinancing,
              or modifying your EMI payments), you should consult with qualified
              banking professionals or licensed financial advisors to obtain
              advice tailored to your personal situation.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              3. Accuracy of Calculations
            </h2>
            <p className="leading-relaxed">
              While we make every effort to ensure the mathematical accuracy of
              our formulas and amortization schedules (such as handling monthly
              compounds, interest-saving algorithms, and tax brackets), banks
              and financial institutions often use slightly different
              compounding methods, daily interest accruals, processing fee
              structures, and grace periods.
            </p>
            <p className="leading-relaxed">
              We do not warrant or guarantee the completeness, accuracy, or
              reliability of the calculations. We are not liable for any
              financial losses or discrepancies arising from the use of this
              website.
            </p>
          </section>

          <section className="space-y-4">
            <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
              4. External Content & Ads
            </h2>
            <p className="leading-relaxed">
              We do not endorse or take responsibility for any third-party
              links, advertisements, features, or loans suggested or linked on
              our website. Clicking on any external link is done entirely at
              your own risk.
            </p>
          </section>

          <section className="space-y-4 pt-6 border-t border-slate-100">
            <h2 className="text-xl font-bold text-slate-900">No Warranties</h2>
            <p className="leading-relaxed">
              THIS WEBSITE AND CALCULATOR TOOLS ARE PROVIDED "AS IS" WITHOUT ANY
              REPRESENTATION OR WARRANTY, EXPRESS OR IMPLIED. SMART EMI PLANNER
              DISCLAIMS ALL WARRANTIES, INCLUDING FITNESS FOR A PARTICULAR
              PURPOSE AND NON-INFRINGEMENT.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
